import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// @mui
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Card, Typography, Box, Stack, Link, Button, Chip, Avatar, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
// components
import Image from '../../components/Image';
import { ProjectAPI } from '../../services/projects.services';
import { addProjectEvent, setGiftCardModal } from '../../redux/slices/donation';
import SnackbarNotification from '../../components/SnackbarNotification';
import Scrollbar from '../../components/Scrollbar';
import Iconify from '../../components/Iconify';
import charityURL from '../../routes/charity.json';
import projectURL from '../../routes/projects.json';
// ----------------------------------------------------------------------

const COLOR_GREEN = '#00A852';
const COLOR_RED = '#FF3D3D';
const COLOR_BLACK = '#000000';
const COLOR_BLUE = '#008CD9';
const COLOR_GRAY = '#747474';
const FONT_MERRIWEATHER = '"Merriweather",serif';

const websiteContents3URL = 'https://charityph-website-content.s3.ap-southeast-1.amazonaws.com';
const projectImagess3URL = 'https://charity-project-images.s3.ap-southeast-1.amazonaws.com';

const charityAvatarCss = {
  color: COLOR_GRAY,
  width: 25,
  height: 25,
  fontSize: 12,
};

const charityDescriptionCss = {
  mt: 2,
  typography: 'body1',
  textAlign: 'left',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: '2',
  WebkitBoxOrient: 'vertical',
};

ProjectSnippets.propTypes = {
  projectContent: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    location: PropTypes.string,
    mainImage: PropTypes.string,
    mainCause: PropTypes.string,
    shortDescription: PropTypes.string,
    isSpecialProject: PropTypes.bool,
    isEmergency: PropTypes.bool,
    projectSnippetDetails: PropTypes.shape({
      headline: PropTypes.string,
      buttonText: PropTypes.string,
    }),
    charityDetails: PropTypes.shape({
      initials: PropTypes.string,
      icon: PropTypes.string,
      name: PropTypes.string,
    }),
  }),
};

function ProjectSnippets({ projectContent }) {
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    _id,
    name,
    location,
    mainImage,
    mainCause,
    projectSnippetDetails,
    isSpecialProject,
    charityDetails,
    shortDescription,
    isEmergency,
  } = projectContent;
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const dispatch = useDispatch();

  const projectHref = projectURL[_id] ?? `project-details/${_id}`;
  const charityHref = charityURL[charityDetails?.id] ?? `CharityId=${charityDetails?.id}`;

  const [params] = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);

  const CharityLogo = () => {
    return charityDetails.icon ? (
      <Avatar src={websiteContents3URL + charityDetails.icon} alt={charityDetails.name} sx={charityAvatarCss}>
        {charityDetails.initials}
      </Avatar>
    ) : (
      <Avatar alt={charityDetails.name} sx={charityAvatarCss}>
        {charityDetails.initials}
      </Avatar>
    );
  };

  const donate = () => {
    if (params.get('ref') === 'giftCard') {
      setIsOpen(true);
    } else {
      setOpenSnackbar(true);
    }
    setTimeout(() => {
      dispatch(addProjectEvent(projectContent));
    }, 500);
  }

  return (
    <>
      <SnackbarNotification
        open={openSnackbar}
        handleClose={() => setOpenSnackbar(false)}
        message={'Project has been successfully added to the giving basket.'}
        projectId={_id}
      />
      <Card
        sx={{
          width: 1,
          height: { md: 300 },
          mt: 3,
          boxShadow: 3,
          borderRadius: 2,
          color: '#000000',
          transition: '.5s ease',
          '& .btn-donate': {
            display: { md: 'none' },
          },
          '& .right-content': {
            display: 'flex',
            flexDirection: 'column',
            // justifyContent: 'center',
          },
          '&:hover': {
            boxShadow: 24,
            '& .btn-donate': {
              display: 'block',
            },
            '& .right-content': {
              // justifyContent: 'flex-start',
            },
          },
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} height="inherit" width="inherit">
          <Image
            sx={{
              height: { xs: 200, md: 'inherit' },
              width: { xs: 1, md: '40%' },
            }}
            alt={name}
            src={(() => mainImage?.includes("https") ? mainImage : (mainImage?.includes('/images/projects') ? websiteContents3URL + mainImage : projectImagess3URL + mainImage) /* eslint-disable-line no-nested-ternary */)()}
          />
          <Chip
            sx={{
              position: 'absolute',
              backgroundColor: 'rgb(0,0,0, .7)',
              color: '#ffffff',
              display: isSpecialProject ? 'flex' : 'none',
              left: 8,
              top: 8,
              '& .MuiChip-icon': { color: '#fff', background: '#000', borderRadius: '50%' },
            }}
            icon={isEmergency ? <Iconify icon={'tabler:urgent'} width={25} height={25} /> : null}
            label="Special Project"
          />
          <Box
            className="right-content"
            sx={{
              px: 4,
              pt: 2,
              pb: 4,
              width: { xs: '100%', md: '60%' },
            }}
          >
            <Box display='flex' alignItems='center' justifyContent='space-between'>
              <Typography
                component={Link}
                variant="h5"
                fontFamily={FONT_MERRIWEATHER}
                sx={{ mb: 2, color: '#000000', '&:hover': { color: COLOR_BLUE } }}
                href={projectHref}
              >
                {name}
              </Typography>
              <Typography
                variant="body2"
                fontWeight={700}
                sx={{
                  width: 170,
                  mb: 2,
                  color: COLOR_GREEN,
                  textAlign: 'right',
                  textTransform: 'capitalize',
                  display: { xs: 'none', md: 'block' },
                }}
              >
                {mainCause}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} alignItems="top">

              {charityDetails?.name !== 'CharityPhilippines.org'
                ?
                <Link
                  // href={`charity?id=${charityDetails?.id}`}
                  href={charityHref}
                  underline="hover"
                  fontWeight={'700 !important'}
                  sx={{
                    color: COLOR_BLACK,
                    typography: 'body2',
                    '&:hover': {
                      color: COLOR_BLUE,
                    },
                  }}
                >
                  {charityDetails?.name}
                </Link>
                :
                <Typography textAlign="left" typography="body2">
                  {charityDetails.name}
                </Typography>
              }

            </Stack>

            <Stack direction="row" spacing={1} justifyContent="flex-start" mt="5px">
              <Typography textAlign="left" typography="body2">
                Project Location:
              </Typography>
              <Typography textAlign="left" variant="body2" fontWeight={700}>
                {location}
              </Typography>
            </Stack>

            <Typography style={{ WebkitLineClamp: 4 }} textAlign="center" sx={charityDescriptionCss}>
              {shortDescription}
            </Typography>
            {!projectContent.isSpecialProject ? (
              <Box
                className="btn-donate"
                component="div"
                sx={{ mt: { xs: 3, md: 2 }, position: { md: 'absolute' }, bottom: { md: 25 }, right: { md: 30 } }}
              >
                <Button
                  fullWidth={smDown}
                  sx={{ width: { md: 200 } }}
                  variant="contained"
                  onClick={donate}
                >
                  Donate
                </Button>
              </Box>
            ) : null}
          </Box>
        </Stack>
      </Card>
      <Dialog open={isOpen}>
        <DialogTitle>Gift Card</DialogTitle>
        <DialogContent>
          <DialogContentText>Do you want to add more projects or do you want to proceed to your gift card?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setIsOpen(false);
            dispatch(setGiftCardModal(true));
          }}>Proceed to Gift Card</Button>
          <Button variant='contained' onClick={() => setIsOpen(false)}>Add more Projects</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function CharitySearchSnippets({ projectContent }) {
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    _id,
    name,
    location,
    mainImage,
    mainCause,
    projectSnippetDetails,
    isSpecialProject,
    charityDetails,
    shortDescription,
    description,
    isEmergency,
  } = projectContent;
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const dispatch = useDispatch();
  const CharityLogo = () => {
    return charityDetails.icon ? (
      <Avatar src={websiteContents3URL + charityDetails.icon} alt={charityDetails.name} sx={charityAvatarCss}>
        {charityDetails.initials}
      </Avatar>
    ) : (
      <Avatar alt={charityDetails.name} sx={charityAvatarCss}>
        {charityDetails.initials}
      </Avatar>
    );
  };

  return (
    <>
      <SnackbarNotification
        open={openSnackbar}
        handleClose={() => setOpenSnackbar(false)}
        message={'Project has been successfully added to the giving basket.'}
        projectId={_id}
      />
      <Card
        sx={{
          width: 1,
          height: { md: 200 },
          mt: 3,
          boxShadow: 3,
          borderRadius: 2,
          color: '#000000',
          transition: '.5s ease',
          '& .btn-donate': {
            display: { md: 'none' },
          },
          '& .right-content': {
            display: 'flex',
            flexDirection: 'column',
            // justifyContent: 'center',
          },
          '&:hover': {
            boxShadow: 24,
            '& .btn-donate': {
              display: 'block',
            },
            '& .right-content': {
              // justifyContent: 'flex-start',
            },
          },
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} height="inherit" width="inherit">
          <Image
            sx={{
              height: { xs: 150, md: 220 },
              width: { xs: 1, md: '40%' },
            }}
            alt={name}
            src={"/images/charityImage.png"}
          />
          <Box
            className="right-content"
            sx={{
              px: 4,
              pt: 2,
              pb: 4,
              width: { xs: '100%', md: '60%' },
            }}
          >
            <Typography
              component={Link}
              variant="h5"
              fontFamily={FONT_MERRIWEATHER}
              sx={{ my: 1, color: '#000000', '&:hover': { color: COLOR_BLUE } }}
              href={`/CharityId=${_id}`}
            >
              {name}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="top">
              {charityDetails?.name !== 'CharityPhilippines.org'
                ?
                <Link
                  href={`charity?id=${charityDetails?.id}`}
                  underline="hover"
                  fontWeight={'700 !important'}
                  sx={{
                    color: COLOR_BLACK,
                    typography: 'body2',
                    '&:hover': {
                      color: COLOR_BLUE,
                    },
                  }}
                >
                  {charityDetails?.name}
                </Link>
                :
                <Typography textAlign="left" typography="body2">
                  {charityDetails.name}
                </Typography>
              }

            </Stack>

            <Typography textAlign="center" sx={charityDescriptionCss}>
              {description[0]}
            </Typography>
          </Box>
        </Stack>
      </Card>
    </>
  );
}


ProjectCard.propTypes = {
  projectList: PropTypes.array,
};

export default function ProjectCard({ projectList, charityList }) {
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    setProjects(projectList);
    return () => {
      setProjects([]);
    };
  }, [projectList]);

  return (
    <Scrollbar sx={{ height: { xs: 1000, md: 1100 }, px: 1.2, borderRadius: 1 }}>
      {projectList && projectList?.map((content) => (
        <ProjectSnippets projectContent={content} key={content._id} />
      ))}
      {charityList && charityList?.map((content) => (
        <CharitySearchSnippets projectContent={content} key={content._id} />
      ))}
    </Scrollbar>
  );
}
